// Catalyst's Radar scale (Ben, 2026-10-26, Q-011 A): each template spoke runs between FIXED numbers,
// copied exactly from Catalyst's own code (soccer-frontend `src/app/players/radar/templates.ts`,
// commit d59d35a, 2026-10-13), not worked out from the players. Same numbers for men and women, as
// Catalyst. Each entry is Catalyst's [min, max]: min at the centre, max at the edge. A "lower is
// better" spoke has min > max (turnovers 6.5 → 2); ours draws those through `isLowerBetter`, so
// `catalystRange` hands back low → high and a test checks the two agree. Percentages: Catalyst
// multiplies a "percentage" stat by 100 before comparing it with these numbers (radar/page.tsx), so
// save % runs 53 → 83; our values are fractions (0.71 = 71%), so `catalystRange` divides by 100.
//
// Only the template's own spokes have numbers. A custom spoke (any other stat) keeps the scale
// worked out from the players (Ben, Q-011 "2a"). Re-copy this table if Catalyst changes its numbers.

import type { RadarRange } from "../modules/radar-view.js";

export const CATALYST_RADAR_SCALE: Record<string, Record<string, readonly [min: number, max: number]>> = {
  striker: {
    np_xg_90: [0.06, 0.76],
    np_shots_90: [1.2, 3.8],
    obv_shot_90: [-0.15, 0.25],
    obv_pass_90: [-0.09, 0.06],
    op_xga_90: [0, 0.25],
    padj_pressures_90: [8, 32],
    aerials_won_90: [0.15, 3.75],
    turnovers_90: [6.5, 2],
    obv_dribble_carry_90: [-0.06, 0.18],
    touches_in_box_90: [0, 15],
    np_xg_per_shot: [0.05, 0.2],
  },
  attacking_midfielder: {
    np_xg_90: [0.04, 0.54],
    np_shots_90: [0.4, 4],
    obv_shot_90: [-0.15, 0.25],
    padj_pressures_90: [9, 34],
    obv_pass_90: [-0.06, 0.26],
    op_xga_90: [0.0, 0.3],
    obv_dribble_carry_90: [-0.02, 0.2],
    fouls_won_90: [0.04, 3],
    turnovers_90: [6, 1.6],
    touches_in_box_90: [0, 10],
    np_xg_per_shot: [0.02, 0.2],
  },
  midfielder: {
    obv_90: [-0.06, 0.4],
    deep_progressions_90: [1, 7],
    obv_pass_90: [-0.06, 0.19],
    op_xga_90: [0.0, 0.2],
    obv_dribble_carry_90: [0.0, 0.16],
    fouls_won_90: [0.2, 2.2],
    turnovers_90: [3.5, 0.5],
    padj_pressures_90: [7, 28],
    padj_tackles_and_interceptions_90: [1, 7],
    challenge_perc: [35, 85],
    obv_defensive_action_90: [-0.02, 0.13],
  },
  centre_back: {
    aerial_perc: [30, 80],
    aerials_won_90: [0.4, 3.4],
    obv_90: [-0.05, 0.3],
    obv_pass_90: [-0.01, 0.17],
    change_in_passing_perc: [-25, 0],
    obv_dribble_carry_90: [0.015, 0.1],
    fouls_committed_90: [1.6, 0],
    padj_clearances_90: [1.5, 7],
    blocks_per_shot: [0.02, 0.12],
    challenge_perc: [50, 100],
    obv_defensive_action_90: [-0.07, 0.17],
  },
  full_back: {
    padj_tackles_and_interceptions_90: [1.5, 7.5],
    deep_progressions_90: [1.5, 7.5],
    obv_pass_90: [0, 0.3],
    op_xga_90: [0.0, 0.16],
    obv_dribble_carry_90: [0, 0.14],
    turnovers_90: [3.4, 0.4],
    aerial_perc: [20, 80],
    padj_pressures_90: [6, 24],
    fouls_committed_90: [1.76, 0.18],
    challenge_perc: [50, 90],
    obv_defensive_action_90: [-0.1, 0.1],
  },
  goalkeeper: {
    obv_gk_90: [-0.6, 0.3],
    long_ball_perc: [30, 60],
    pass_into_danger_perc: [28, 4],
    claimable_claim_success_perc: [50, 100],
    gk_average_x_aggressive_action: [6.5, 13],
    np_save_perc: [53, 83],
    np_xg_positioning_suppression_90: [-0.12, 0.28],
    np_xg_stopping_suppression_90: [-0.5, 0.35],
    np_xg_save_difficulty_faced_90: [0.4, 2.3],
    np_xg_faced_90: [0.5, 2],
  },
};

/** Catalyst's fixed scale for a template spoke, low → high (our radar inverts a lower-is-better
 *  spoke itself), in our units (a percentage as a fraction), or null when Catalyst has none (a
 *  custom spoke). Pure. */
export function catalystRange(template: string, key: string, outputType: string): RadarRange | null {
  const bounds = CATALYST_RADAR_SCALE[template]?.[key];
  if (!bounds) return null;
  const unit = outputType === "percentage" ? 100 : 1;
  return { min: Math.min(bounds[0], bounds[1]) / unit, max: Math.max(bounds[0], bounds[1]) / unit };
}
